import React, { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import {
    Column,
    DataGrid,
    Selection,
    Export,
    Sorting,
    Paging,
    Toolbar,
    Item,
    Editing,
    FilterRow,
    Lookup,
    RequiredRule
} from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import { createConcurrencyAwareStore, getStoreErrorMessage, refreshGridOnConflict } from "../../../../../../common/dataStore";
import { Button } from 'devextreme-react/button';
import { DropDownButton } from 'devextreme-react/drop-down-button';
import { LoadPanel } from 'devextreme-react/load-panel';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import axios from "axios";
import routes from "../../../../../../assets/apiRoutes.json";
import { EMPTY_GUID, CommissionBasisType, CommissionMessages, CurrencyTypes } from "../../../../../../enum/Enum";
import CommissionsCurrencyPopup, { CURRENCY_FIELDS } from "../../common/CommissionsCurrencyPopup";

// SPOPENITEMS.BASISTYPE — how Amount is derived from Rate and Basis (OMD0000 edtBasisTypeChange).
const basisTypeOptions = [
    { id: CommissionBasisType.Percentage, text: 'Percentage' },
    { id: CommissionBasisType.PerUnit, text: 'Per Unit' },
    { id: CommissionBasisType.Flat, text: 'Flat' },
];

// Committed commission rows (U/Z) are read-only per row (OMD0000 SetCommissionsEnabledDisabledFields).
const committedRecordTypes = ['U', 'Z'];
const isCommittedRow = (row) => committedRecordTypes.includes(row?.RecordType);

// Options menu of the Commissions toolbar (OMD0000 pmSPOpenItems).
const showCurrencyFormItemId = 'ShowCommissionsCurrencyForm';

// Client-side mirror of the server ComputeCommissionAmount so the Amount cell updates live.
function computeAmount(basisType, basis, rate) {
    const r = Number(rate) || 0;
    const b = Number(basis) || 0;
    if (basisType === CommissionBasisType.Percentage) return (r / 100) * b;
    if (basisType === CommissionBasisType.Flat) return r;
    return r * b; // Per Unit / default
}

// A foreign line keeps its Basis / Rate / Amount in the FOREIGN* fields (OMD0000 SetCommissionsCurrencyFields and
// grdBottomDetailsParticipantsDrawColumnCell), so the same three columns read and write whichever set the line's
// transaction currency uses — as the server's ComputeLineAmount does.
const homeAmountFields = { basis: 'Basis', rate: 'Rate', amount: 'Amount' };
const foreignAmountFields = { basis: 'ForeignBasis', rate: 'ForeignRate', amount: 'ForeignAmount' };
const amountFieldsOf = (row) => (row?.TransCurrencyType === CurrencyTypes.Foreign ? foreignAmountFields : homeAmountFields);
const basisCellValue = (row) => row?.[amountFieldsOf(row).basis];
const rateCellValue = (row) => row?.[amountFieldsOf(row).rate];
const amountCellValue = (row) => row?.[amountFieldsOf(row).amount];

function setBasisTypeValue(newData, value, currentRow) {
    const fields = amountFieldsOf(currentRow);
    newData.BasisType = value;
    newData[fields.amount] = computeAmount(value, currentRow[fields.basis], currentRow[fields.rate]);
}
function setBasisValue(newData, value, currentRow) {
    const fields = amountFieldsOf(currentRow);
    newData[fields.basis] = value;
    newData[fields.amount] = computeAmount(currentRow.BasisType, value, currentRow[fields.rate]);
}
function setRateValue(newData, value, currentRow) {
    const fields = amountFieldsOf(currentRow);
    newData[fields.rate] = value;
    newData[fields.amount] = computeAmount(currentRow.BasisType, currentRow[fields.basis], value);
}
function setAmountValue(newData, value, currentRow) {
    newData[amountFieldsOf(currentRow).amount] = value;
}

function CommissionsTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    // The currently selected transaction-detail line owns these commission rows.
    const detailRowId = props.formFields?.Rowid;
    const hasDetail = !!detailRowId && detailRowId !== EMPTY_GUID;
    // Committed orders are read-only (OMD0000: grid greyed when header STATUS = 'Z').
    const isReadOnly = !!props.isReadOnly;
    const canEdit = hasDetail && !isReadOnly;

    const commissionGrid = useRef(null);
    const [selectedKeys, setSelectedKeys] = useState([]);
    const [participants, setParticipants] = useState([]);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const hideLoadPanel = useCallback(() => setLoadPanelVisible(false), []);
    const [fxInUse, setFxInUse] = useState(false);
    // The commission line the currency form is open for (OMD0000 FCommissionCurrency), or null.
    const [currencyRow, setCurrencyRow] = useState(null);
    const closeCurrencyForm = useCallback(() => setCurrencyRow(null), []);

    // The currency form is only offered when foreign exchange is in use (OMD0000 pmSPOpenItemsPopup: AB_FXINUSE).
    useEffect(() => {
        if (!accessToken) return;
        let cancelled = false;
        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.AC.GetForeignCurrencyInUse}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            })
            .then((res) => {
                if (!cancelled) setFxInUse(res.data === true);
            })
            .catch(() => {
                if (!cancelled) setFxInUse(false);
            });
        return () => {
            cancelled = true;
        };
    }, [accessToken]);

    // Moving to another detail line hides the form (OMD0000 pagBottomDetailChange -> HideCommissionsCurrencyForm).
    useEffect(() => {
        setCurrencyRow(null);
    }, [detailRowId]);

    // Participant (salesperson) pick-list — reused from the Sales Order lookup endpoint.
    useEffect(() => {
        if (!accessToken) return;
        let cancelled = false;
        axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetSalePersons}`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            })
            .then((res) => {
                if (cancelled) return;
                const list = res.data?.Lookups ?? res.data?.lookups ?? [];
                setParticipants(
                    list.map((p) => ({
                        rowid: p.Rowid ?? p.rowid,
                        code: (p.Code ?? p.code ?? "").trim(),
                    }))
                );
            })
            .catch(() => {
                if (!cancelled) setParticipants([]);
            });
        return () => {
            cancelled = true;
        };
    }, [accessToken]);

    // A stale edit or delete re-pulls this grid instead of reloading the page (Architecture rule 16).
    const onConflict = useMemo(() => refreshGridOnConflict(commissionGrid), []);

    const dataSource = useMemo(() => {
        if (!hasDetail) {
            return new DataSource({ store: [], key: "RowId" });
        }
        return new DataSource({
            store: createConcurrencyAwareStore({
                onConflict,
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.GetOpenItems}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.AddOpenItem}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.EditOpenItem}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${routes.OM.DeleteOpenItem}`,
                loadParams: { detailRowId },
                onBeforeSend: function (operation) {
                    // Auth headers and concurrency detection come from the shared wrapper. Standard DevExtreme
                    // protocol otherwise: the grid posts form-encoded values/key, and key + parent FK are
                    // stamped in onRowInserting/onRowUpdating, not here.
                    // Show the full-screen loader for the duration of an add/edit/delete round-trip.
                    if (operation === "insert" || operation === "update" || operation === "delete") {
                        setLoadPanelVisible(true);
                    }
                },
                // Fires after any insert/update/delete resolves — clear the loader.
                onModified: function () {
                    setLoadPanelVisible(false);
                },
                errorHandler: function () {
                    setLoadPanelVisible(false);
                },
                // The API returns the full row (with computed Amount + Cumulative), so DevExtreme
                // patches in place (refreshMode="repaint") — no manual grid.refresh() needed.
                onInserted: function () {
                    notify(CommissionMessages.Added, "success", 3000);
                },
                onUpdated: function () {
                    notify(CommissionMessages.Updated, "success", 3000);
                },
                onRemoved: function () {
                    notify(CommissionMessages.Deleted, "success", 3000);
                },
            }, accessToken),
            key: "RowId",
            paginate: false,
        });
    }, [detailRowId, hasDetail, accessToken, onConflict]);

    const onSelectionChanged = useCallback((e) => {
        setSelectedKeys(e.selectedRowKeys);
    }, []);

    const onInitNewRow = useCallback((e) => {
        e.data.RowId = EMPTY_GUID;
        e.data.RSourceId = detailRowId;
        e.data.BasisType = CommissionBasisType.PerUnit;
        e.data.Description = '';
        // The line's currency set comes first (OMD0000 qeSPOpenItemsAfterInsert), so on a foreign line the zeros
        // below — and everything typed after — land in its FOREIGN* fields.
        e.promise = axios
            .get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetOpenItemDefaults}`, {
                params: { detailRowId },
                headers: { Authorization: `Bearer ${accessToken}` },
            })
            .then((res) => {
                CURRENCY_FIELDS.forEach((field) => {
                    e.data[field] = res.data?.[field] ?? null;
                });
            })
            .catch(() => {
                // AddOpenItem applies the same defaults when the row arrives without a currency set.
            })
            .then(() => {
                const fields = amountFieldsOf(e.data);
                e.data[fields.basis] = 0;
                e.data[fields.rate] = 0;
                e.data[fields.amount] = 0;
            });
    }, [detailRowId, accessToken]);

    // Stamp the key + parent FK so they land inside the posted "values" JSON.
    const onRowInserting = useCallback((e) => {
        e.data.RowId = EMPTY_GUID;
        e.data.RSourceId = detailRowId;
    }, [detailRowId]);

    // Cell editing sends only the changed cell — merge so unchanged columns aren't wiped.
    const onRowUpdating = useCallback((e) => {
        e.newData = { ...e.oldData, ...e.newData };
        e.newData.RSourceId = detailRowId;
    }, [detailRowId]);

    // On participant select, auto-fill Basis Type / Basis / Rate / Amount from the SP settlement-payables
    // setup (OMD0000 edtP_ParticipantExit -> CalculateSPCommissions). If no setup matches, manual entry stays.
    // The defaults are priced in home currency, so they fill the home fields even on a foreign line.
    const setParticipantValue = useCallback(async (newData, value) => {
        newData.RIdentity = value;
        if (!value || !hasDetail || !accessToken) return;
        try {
            const res = await axios.get(`${window._env_.REACT_APP_APIS}/${routes.OM.GetCommissionDefaults}`, {
                params: { detailRowId, participantRowId: value },
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            const d = res.data;
            if (d && d.BasisType) {
                newData.BasisType = d.BasisType;
                newData.Basis = d.Basis ?? 0;
                newData.Rate = d.Rate ?? 0;
                newData.Amount = d.Amount ?? computeAmount(d.BasisType, d.Basis, d.Rate);
            }
        } catch {
            // No settlement setup / lookup failed — keep manual entry.
        }
    }, [detailRowId, hasDetail, accessToken]);

    const onDeleteSelected = useCallback(() => {
        const grid = commissionGrid.current?.instance?.();
        if (!grid) return;
        const keys = grid.getSelectedRowKeys();
        if (!keys.length) return;
        confirm("Delete selected record?", "Confirm delete").then((yes) => {
            if (!yes) return;
            const store = grid.getDataSource().store();
            Promise.allSettled(keys.map((k) => store.remove(k))).then((results) => {
                results
                    .filter((r) => r.status === "rejected")
                    .forEach((r) => {
                        // Null for a concurrency conflict: the dialog is already up (and refreshes this grid
                        // with the same action the store uses), so no red toast on top of it.
                        const message = getStoreErrorMessage(r.reason, CommissionMessages.DeleteFailed, onConflict);
                        if (message) notify(message, "error", 5000);
                    });
                grid.refresh();
            });
        });
    }, [onConflict]);

    // Disable editing per row for committed lines (U/Z) even inside an open order (OMD0000 parity).
    const onEditorPreparing = useCallback((e) => {
        if (e.parentType === "dataRow" && isCommittedRow(e.row?.data)) {
            e.editorOptions.disabled = true;
        }
    }, []);

    // Delphi bound the currency form to the grid's current record; here it is the one selected, saved line.
    const selectedLineKey = selectedKeys.length === 1 && selectedKeys[0] !== EMPTY_GUID ? selectedKeys[0] : null;

    const optionItems = useMemo(() => [
        {
            id: showCurrencyFormItemId,
            name: CommissionMessages.ShowCurrencyForm,
            disabled: !fxInUse || !selectedLineKey,
        },
    ], [fxInUse, selectedLineKey]);

    const onOptionItemClick = useCallback((e) => {
        if (e.itemData?.id !== showCurrencyFormItemId || e.itemData.disabled) return;
        const grid = commissionGrid.current?.instance?.();
        const row = grid?.getVisibleRows().find((r) => r.rowType === "data" && r.key === selectedLineKey);
        if (row) setCurrencyRow(row.data);
    }, [selectedLineKey]);

    // Save the form through the grid's own edit pipeline, exactly like an inline cell edit (the currency
    // fields are hidden columns so cellValue can set them): onRowUpdating merges the row, EditOpenItem
    // returns the full row and the grid patches it in place.
    const applyCurrencyChanges = useCallback((key, changes) => {
        const grid = commissionGrid.current?.instance?.();
        const rowIndex = grid ? grid.getRowIndexByKey(key) : -1;
        if (rowIndex < 0) return;
        Object.entries(changes).forEach(([field, value]) => grid.cellValue(rowIndex, field, value));
        grid.saveEditData();
    }, []);

    return (
        <div style={{ height: '100%', overflow: 'auto' }}>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={{ of: window }}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />
            <DataGrid
                dataSource={dataSource}
                ref={commissionGrid}
                selectedRowKeys={selectedKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={onSelectionChanged}
                onInitNewRow={onInitNewRow}
                onRowInserting={onRowInserting}
                onRowUpdating={onRowUpdating}
                onEditorPreparing={onEditorPreparing}
                height="100%"
            >
                <Selection mode="multiple" showCheckBoxesMode="always" allowSelectAll={false} />
                <Export enabled={true} allowExportSelectedData={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowAdding={canEdit}
                    allowUpdating={canEdit}
                    allowDeleting={false}
                />
                <FilterRow visible={true} />
                <Sorting mode="none" />
                <Paging enabled={false} />

                <Column dataField="RowId" caption="Row ID" visible={false} showInColumnChooser={false} />
                <Column dataField="RSourceId" caption="Detail ID" visible={false} showInColumnChooser={false} />
                <Column dataField="RIdentity" caption="Participant" allowSorting={false} setCellValue={setParticipantValue}>
                    <RequiredRule message={CommissionMessages.ParticipantRequired} />
                    <Lookup
                        dataSource={participants}
                        valueExpr="rowid"
                        displayExpr="code"
                    />
                </Column>
                <Column
                    dataField="ParticipantName"
                    caption="Name"
                    dataType="string"
                    allowEditing={false}
                    allowSorting={false}
                />
                <Column dataField="BasisType" caption="Basis Type" allowSorting={false} setCellValue={setBasisTypeValue}>
                    <Lookup
                        dataSource={basisTypeOptions}
                        valueExpr="id"
                        displayExpr="text"
                    />
                </Column>
                {/* Unbound: each reads/writes the home or FOREIGN* field per the line's currency (amountFieldsOf). */}
                <Column
                    name="Basis"
                    caption="Basis"
                    dataType="number"
                    format={{ type: 'fixedPoint', precision: 2 }}
                    allowSorting={false}
                    calculateCellValue={basisCellValue}
                    setCellValue={setBasisValue}
                />
                <Column
                    name="Rate"
                    caption="Rate"
                    dataType="number"
                    format={{ type: 'fixedPoint', precision: 4 }}
                    allowSorting={false}
                    calculateCellValue={rateCellValue}
                    setCellValue={setRateValue}
                />
                <Column
                    name="Amount"
                    caption="Amount"
                    dataType="number"
                    format={{ type: 'currency', precision: 2 }}
                    allowSorting={false}
                    calculateCellValue={amountCellValue}
                    setCellValue={setAmountValue}
                />
                <Column
                    dataField="Cumulative"
                    caption="Cumulative"
                    dataType="number"
                    format={{ type: 'currency', precision: 2 }}
                    allowEditing={false}
                    allowSorting={false}
                />
                <Column
                    dataField="Description"
                    caption="Description"
                    dataType="string"
                    allowSorting={false}
                />
                {/* Currency set, edited in the Commissions Currency popup (uOMD0012), not in the grid. */}
                <Column dataField="TransCurrencyType" visible={false} showInColumnChooser={false} />
                <Column dataField="RTransCurrencyCode" visible={false} showInColumnChooser={false} />
                <Column dataField="PayCurrencyType" visible={false} showInColumnChooser={false} />
                <Column dataField="RPayCurrencyCode" visible={false} showInColumnChooser={false} />
                <Column dataField="RateTiming" visible={false} showInColumnChooser={false} />
                <Column dataField="CurrencyRate" dataType="number" visible={false} showInColumnChooser={false} />
                <Toolbar>
                    <Item name="addRowButton" location="after" disabled={!canEdit} />
                    <Item location="after" locateInMenu="never">
                        <Button
                            icon="trash"
                            hint="Delete selected"
                            stylingMode="text"
                            disabled={!canEdit || selectedKeys.length === 0}
                            onClick={onDeleteSelected}
                        />
                    </Item>
                    <Item location="after" locateInMenu="never">
                        <DropDownButton
                            icon="fa fa-cogs"
                            hint="Options"
                            stylingMode="text"
                            items={optionItems}
                            displayExpr="name"
                            keyExpr="id"
                            onItemClick={onOptionItemClick}
                            disabled={!hasDetail}
                            dropDownOptions={{ width: 260 }}
                        />
                    </Item>
                    <Item name="saveButton" />
                    <Item name="revertButton" />
                    <Item name="exportButton" />
                </Toolbar>
            </DataGrid>
            {currencyRow && (
                <CommissionsCurrencyPopup
                    row={currencyRow}
                    readOnly={!canEdit || isCommittedRow(currencyRow)}
                    onClose={closeCurrencyForm}
                    onApply={(changes) => applyCurrencyChanges(currencyRow.RowId, changes)}
                />
            )}
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CommissionsTab);
